import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/store';
import type { EventCard } from '../lib/types';
import { Bar, Panel } from './ui';
import { formatNumber } from '../lib/format';

/**
 * The Home page's event strip.
 *
 * PART 34 asks that a running event be visible where players already are,
 * rather than only on a page they have to remember to visit — the same
 * reasoning that put the Community Project meter here.
 *
 * It renders **nothing at all** when no event is running. An empty panel saying
 * "no events" is a permanent hole in the Home page in exchange for information
 * nobody needed; the Events page is where you go to find out that nothing is on.
 *
 * The pull is the *next reward*, not the points total. "1,420 points" is a
 * status line; "340 more for the Autumn Frame" is a reason to open it.
 */
export function EventPanel() {
  const state = useAsync<{ events: Array<EventCard & { nextReward: { threshold: number; label: string } | null }> }>(
    () => api.get('/events/home/summary'),
  );

  const events = state.data?.events ?? [];
  if (state.loading || events.length === 0) return null;

  return (
    <>
      {events.map((event) => (
        <Panel
          key={event.key}
          title={event.name}
          hint={countdown(event.secondsRemaining)}
          className={`event-theme--${event.theme}`}
          action={
            <Link to={`/events/${event.key}`} className="btn btn--sm">
              View event
            </Link>
          }
        >
          <div className="stack-sm">
            {event.summary && <div className="muted">{event.summary}</div>}

            {event.nextReward ? (
              <>
                <Bar
                  ratio={Math.min(1, event.myPoints / Math.max(1, event.nextReward.threshold))}
                />
                <div className="event-home__next">
                  <span>
                    {formatNumber(event.myPoints)} / {formatNumber(event.nextReward.threshold)}{' '}
                    {event.pointsName ?? 'points'}
                  </span>
                  <span className="muted">Next: {event.nextReward.label}</span>
                </div>
              </>
            ) : (
              // Either they have taken everything, or the event has no track.
              // Both are "nothing to chase", and neither deserves a full bar.
              <div className="muted">
                {event.myPoints > 0
                  ? `${formatNumber(event.myPoints)} ${event.pointsName ?? 'points'} earned — every reward collected.`
                  : 'Take part however you already play.'}
              </div>
            )}
          </div>
        </Panel>
      ))}
    </>
  );
}

function countdown(seconds: number): string {
  if (seconds <= 0) return 'Ending now';
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3600);
  if (days > 0) return `${days}d ${hours}h left`;
  const minutes = Math.floor((seconds % 3600) / 60);
  return hours > 0 ? `${hours}h ${minutes}m left` : `${minutes}m left`;
}
